import { useState, useMemo } from "react";
import {
  Container,
  Box,
  Title,
  Text,
  Stack,
  Modal,
  Button,
  Group,
  Badge,
  Select,
  Paper,
  TextInput,
  Table,
  Pagination,
  ActionIcon,
  Tooltip,
  Loader,
  Center,
} from "@mantine/core";
import { useDisclosure, useDebouncedValue } from "@mantine/hooks";
import {
  IconSearch,
  IconPlus,
  IconEdit,
  IconFilter,
  IconRefresh,
  IconCategory,
  IconCheck,
  IconX,
} from "@tabler/icons-react";
import {
  useDiagnosisCategoriesAdmin,
  useCreateDiagnosisCategory,
  useUpdateDiagnosisCategory,
  useToggleDiagnosisCategoryActive,
} from "../../../hooks/admin/useDiagnosisCategories";
import DiagnosisCategoryForm, {
  DiagnosisCategoryFormValues,
} from "../../../components/admin/diagnosiscategories/DiagnosisCategoryForm";
import { DiagnosisCategory } from "../../../services/admin/diagnosiscategories.service";

interface DiagnosisCategoryFilters {
  status?: "active" | "inactive";
  markerShapes?: string[];
}

const ITEMS_PER_PAGE = 20;

function MarkerPreviewInline({
  shape,
  color,
}: {
  shape: string | null;
  color: string | null;
}) {
  if (!shape && !color)
    return (
      <Text size="sm" c="dimmed">
        —
      </Text>
    );

  const previewColor = color || "#868e96";
  const size = 18;

  const shapeElement = () => {
    switch (shape) {
      case "circle":
        return (
          <svg width={size} height={size} viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10" fill={previewColor} />
          </svg>
        );
      case "square":
        return (
          <svg width={size} height={size} viewBox="0 0 24 24">
            <rect x="2" y="2" width="20" height="20" fill={previewColor} />
          </svg>
        );
      case "triangle":
        return (
          <svg width={size} height={size} viewBox="0 0 24 24">
            <polygon points="12,2 22,22 2,22" fill={previewColor} />
          </svg>
        );
      case "diamond":
        return (
          <svg width={size} height={size} viewBox="0 0 24 24">
            <polygon points="12,2 22,12 12,22 2,12" fill={previewColor} />
          </svg>
        );
      case "star":
        return (
          <svg width={size} height={size} viewBox="0 0 24 24">
            <polygon
              points="12,2 15,9 22,9 16.5,14 18.5,22 12,17.5 5.5,22 7.5,14 2,9 9,9"
              fill={previewColor}
            />
          </svg>
        );
      default:
        return (
          <svg width={size} height={size} viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10" fill={previewColor} />
          </svg>
        );
    }
  };

  return (
    <Group gap="xs" align="center">
      {shapeElement()}
      <Text size="xs" c="dimmed" tt="capitalize">
        {shape || "—"}
      </Text>
    </Group>
  );
}

export default function DiagnosisCategoriesAdminPage() {
  // Modal states
  const [formModalOpened, { open: openFormModal, close: closeFormModal }] =
    useDisclosure(false);
  const [
    filterModalOpened,
    { open: openFilterModal, close: closeFilterModal },
  ] = useDisclosure(false);
  const [
    confirmModalOpened,
    { open: openConfirmModal, close: closeConfirmModal },
  ] = useDisclosure(false);

  // Page state
  const [editing, setEditing] = useState<DiagnosisCategory | null>(null);
  const [togglingItem, setTogglingItem] = useState<DiagnosisCategory | null>(
    null
  );
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [filters, setFilters] = useState<DiagnosisCategoryFilters>({});
  const [pendingFilters, setPendingFilters] =
    useState<DiagnosisCategoryFilters>({});
  const [page, setPage] = useState(1);

  // Data hooks
  const {
    data: categories = [],
    isLoading,
    refetch,
  } = useDiagnosisCategoriesAdmin();
  const createCategory = useCreateDiagnosisCategory();
  const updateCategory = useUpdateDiagnosisCategory();
  const toggleActive = useToggleDiagnosisCategoryActive();

  // Client-side filtering
  const filteredData = useMemo(() => {
    let result = categories;

    // Search filter
    if (debouncedSearch) {
      const searchLower = debouncedSearch.toLowerCase();
      result = result.filter(
        (item) =>
          item.name.toLowerCase().includes(searchLower) ||
          (item.description &&
            item.description.toLowerCase().includes(searchLower))
      );
    }

    // Status filter
    if (filters.status) {
      result = result.filter((item) =>
        filters.status === "active" ? item.isActive : !item.isActive
      );
    }

    return result;
  }, [categories, debouncedSearch, filters]);

  // Pagination
  const totalPages = Math.ceil(filteredData.length / ITEMS_PER_PAGE);
  const paginatedData = useMemo(() => {
    const start = (page - 1) * ITEMS_PER_PAGE;
    return filteredData.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredData, page]);

  // Check if any filters are active
  const hasActiveFilters = filters.status !== undefined;

  // Handlers
  const handleAdd = () => {
    setEditing(null);
    openFormModal();
  };

  const handleEdit = (item: DiagnosisCategory) => {
    setEditing(item);
    openFormModal();
  };

  const handleSubmit = async (values: DiagnosisCategoryFormValues) => {
    const sanitizedValues = {
      ...values,
      markerShape: values.markerShape || null,
      markerColor: values.markerColor || null,
      description: values.description || null,
    };

    if (editing) {
      await updateCategory.mutateAsync({
        id: editing.id,
        ...sanitizedValues,
      });
    } else {
      await createCategory.mutateAsync(sanitizedValues);
    }
    closeFormModal();
  };

  const handleToggleActive = (item: DiagnosisCategory) => {
    setTogglingItem(item);
    openConfirmModal();
  };

  const confirmToggleActive = async () => {
    if (!togglingItem) return;
    await toggleActive.mutateAsync({
      id: togglingItem.id,
      isActive: !togglingItem.isActive,
    });
    closeConfirmModal();
    setTogglingItem(null);
  };

  const handleClearAllFilters = () => {
    setFilters({});
    setPendingFilters({});
    setSearch("");
    setPage(1);
  };

  const handleOpenFilterModal = () => {
    setPendingFilters({ ...filters });
    openFilterModal();
  };

  const handleApplyFilters = () => {
    setFilters({ ...pendingFilters });
    setPage(1);
    closeFilterModal();
  };

  const handleClearPendingFilters = () => {
    setPendingFilters({});
  };

  // Options
  const statusOptions = [
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
  ];

  return (
    <Container fluid py="xl">
      <Box style={{ maxWidth: "1600px", margin: "0 auto" }}>
        <Stack gap="lg">
          {/* Header */}
          <Group justify="space-between" align="flex-start">
            <div>
              <Group gap="sm">
                <IconCategory size={28} color="var(--mantine-color-blue-6)" />
                <Title order={2}>Diagnosis Categories</Title>
              </Group>
              <Text size="sm" c="dimmed" mt="xs">
                Manage diagnosis categories for classification. Categories group
                diagnoses and define organ map marker appearance.
              </Text>
            </div>
            <Button leftSection={<IconPlus size={16} />} onClick={handleAdd}>
              Add New
            </Button>
          </Group>

          {/* Search and Filters Row */}
          <Group align="stretch" grow>
            {/* Search Section */}
            <Paper p="md" withBorder style={{ flex: 2 }}>
              <Stack gap="sm">
                <Group>
                  <IconSearch size={20} />
                  <Text fw={500}>Search</Text>
                </Group>
                <TextInput
                  placeholder="Search by name or description..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  leftSection={<IconSearch size={16} />}
                />
              </Stack>
            </Paper>

            {/* Filters Section */}
            <Paper p="md" withBorder style={{ flex: 1 }}>
              <Stack gap="sm">
                <Group justify="space-between">
                  <Group>
                    <IconFilter size={20} />
                    <Text fw={500}>Filters</Text>
                  </Group>
                  <Button
                    variant="subtle"
                    size="compact-sm"
                    onClick={() => refetch()}
                    loading={isLoading}
                    leftSection={<IconRefresh size={16} />}
                  />
                </Group>
                <Button
                  variant="light"
                  onClick={handleOpenFilterModal}
                  fullWidth
                >
                  {hasActiveFilters ? "Edit Filters" : "Add Filters"}
                </Button>
                {hasActiveFilters && (
                  <Group gap="xs" wrap="wrap">
                    <Text size="sm" c="dimmed">
                      Active:
                    </Text>
                    {filters.status && (
                      <Badge
                        variant="light"
                        color={filters.status === "active" ? "green" : "red"}
                      >
                        {filters.status === "active" ? "Active" : "Inactive"}
                      </Badge>
                    )}
                    <Button
                      variant="subtle"
                      size="compact-xs"
                      color="gray"
                      onClick={handleClearAllFilters}
                    >
                      Clear All
                    </Button>
                  </Group>
                )}
                <Text size="sm" c="dimmed">
                  {filteredData.length} categor
                  {filteredData.length !== 1 ? "ies" : "y"} found
                </Text>
              </Stack>
            </Paper>
          </Group>

          {/* Table Section */}
          <Paper p="md" withBorder>
            <Stack gap="md">
              {/* Table Header with Pagination */}
              <Group justify="space-between">
                <Text fw={500} size="lg">
                  Diagnosis Categories ({filteredData.length})
                </Text>
                {totalPages > 1 && (
                  <Group gap="md">
                    <Text size="sm" c="dimmed">
                      Showing {(page - 1) * ITEMS_PER_PAGE + 1}-
                      {Math.min(page * ITEMS_PER_PAGE, filteredData.length)} of{" "}
                      {filteredData.length}
                    </Text>
                    <Pagination
                      total={totalPages}
                      value={page}
                      onChange={setPage}
                      size="sm"
                    />
                  </Group>
                )}
              </Group>

              {/* Table */}
              {isLoading ? (
                <Center p="xl">
                  <Loader />
                </Center>
              ) : filteredData.length === 0 ? (
                <Text c="dimmed" ta="center" py="xl">
                  {hasActiveFilters || search
                    ? "No categories match the current filters"
                    : "No diagnosis categories found"}
                </Text>
              ) : (
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Name</Table.Th>
                      <Table.Th>Description</Table.Th>
                      <Table.Th style={{ width: 150 }}>Marker</Table.Th>
                      <Table.Th style={{ width: 100 }}>Status</Table.Th>
                      <Table.Th style={{ width: 100 }}>Actions</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {paginatedData.map((item) => (
                      <Table.Tr
                        key={item.id}
                        style={{ opacity: item.isActive ? 1 : 0.5 }}
                      >
                        {/* Name */}
                        <Table.Td>
                          <Text size="sm" fw={500}>
                            {item.name}
                          </Text>
                        </Table.Td>

                        {/* Description */}
                        <Table.Td>
                          <Text size="sm" lineClamp={2}>
                            {item.description || "—"}
                          </Text>
                        </Table.Td>

                        {/* Marker Preview */}
                        <Table.Td>
                          <MarkerPreviewInline
                            shape={item.markerShape}
                            color={item.markerColor}
                          />
                        </Table.Td>

                        {/* Status */}
                        <Table.Td>
                          <Badge
                            color={item.isActive ? "green" : "red"}
                            variant="light"
                          >
                            {item.isActive ? "Active" : "Inactive"}
                          </Badge>
                        </Table.Td>

                        {/* Actions */}
                        <Table.Td>
                          <Group gap="xs">
                            <Tooltip label="Edit">
                              <ActionIcon
                                variant="subtle"
                                color="blue"
                                onClick={() => handleEdit(item)}
                              >
                                <IconEdit size={18} />
                              </ActionIcon>
                            </Tooltip>
                            <Tooltip
                              label={item.isActive ? "Deactivate" : "Activate"}
                            >
                              <ActionIcon
                                variant="subtle"
                                color={item.isActive ? "red" : "green"}
                                onClick={() => handleToggleActive(item)}
                              >
                                {item.isActive ? (
                                  <IconX size={18} />
                                ) : (
                                  <IconCheck size={18} />
                                )}
                              </ActionIcon>
                            </Tooltip>
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              )}
            </Stack>
          </Paper>
        </Stack>

        {/* Create/Edit Modal */}
        <Modal
          opened={formModalOpened}
          onClose={closeFormModal}
          title={
            editing ? "Edit Diagnosis Category" : "Add New Diagnosis Category"
          }
          size="lg"
          closeOnClickOutside={false}
        >
          <Stack gap="md">
            <DiagnosisCategoryForm
              initialValues={editing || undefined}
              onSubmit={handleSubmit}
            />

            <Group justify="flex-end" mt="md">
              <Button variant="subtle" onClick={closeFormModal}>
                Cancel
              </Button>
              <Button
                type="submit"
                loading={createCategory.isPending || updateCategory.isPending}
                onClick={() => {
                  const form = document.querySelector("form");
                  if (form) form.requestSubmit();
                }}
              >
                {editing ? "Update" : "Create"}
              </Button>
            </Group>
          </Stack>
        </Modal>

        {/* Filter Modal */}
        <Modal
          opened={filterModalOpened}
          onClose={closeFilterModal}
          title="Filter Diagnosis Categories"
          size="md"
          closeOnClickOutside={false}
        >
          <Stack gap="md">
            <Select
              label="Status"
              placeholder="All statuses"
              data={statusOptions}
              value={pendingFilters.status || null}
              onChange={(value) =>
                setPendingFilters({
                  ...pendingFilters,
                  status: value as "active" | "inactive" | undefined,
                })
              }
              clearable
            />

            <Group justify="flex-end" mt="md">
              <Button variant="subtle" onClick={handleClearPendingFilters}>
                Clear All
              </Button>
              <Button onClick={handleApplyFilters}>Apply Filters</Button>
            </Group>
          </Stack>
        </Modal>

        {/* Confirmation Modal for Activate/Deactivate */}
        <Modal
          opened={confirmModalOpened}
          onClose={() => {
            closeConfirmModal();
            setTogglingItem(null);
          }}
          title={
            togglingItem?.isActive
              ? "Deactivate Diagnosis Category"
              : "Activate Diagnosis Category"
          }
          size="sm"
          centered
        >
          <Stack gap="md">
            <Text>
              Are you sure you want to{" "}
              <Text span fw={600}>
                {togglingItem?.isActive ? "deactivate" : "activate"}
              </Text>{" "}
              the category{" "}
              <Text span fw={600}>
                &quot;{togglingItem?.name}&quot;
              </Text>
              ?
            </Text>
            {togglingItem?.isActive && (
              <Text size="sm" c="dimmed">
                Deactivating this category will hide it from the diagnosis
                selection dropdown. Existing diagnoses in this category will not
                be affected.
              </Text>
            )}
            <Group justify="flex-end" mt="md">
              <Button
                variant="subtle"
                onClick={() => {
                  closeConfirmModal();
                  setTogglingItem(null);
                }}
              >
                Cancel
              </Button>
              <Button
                color={togglingItem?.isActive ? "red" : "green"}
                loading={toggleActive.isPending}
                onClick={confirmToggleActive}
              >
                {togglingItem?.isActive ? "Deactivate" : "Activate"}
              </Button>
            </Group>
          </Stack>
        </Modal>
      </Box>
    </Container>
  );
}
